import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Hotel, 
  ShoppingBag, 
  Factory, 
  UtensilsCrossed, 
  Heart, 
  Building,
  ArrowRight
} from 'lucide-react'


export const IndustryShowcase: React.FC = () => {
  const { t } = useTranslation('workforce')
  
  const industries = [
    {
      icon: Hotel,
      name: t('industryShowcase.industries.hospitality.name'),
      description: t('industryShowcase.industries.hospitality.description'),
      examples: [
        t('industryShowcase.industries.hospitality.examples.0'),
        t('industryShowcase.industries.hospitality.examples.1'),
        t('industryShowcase.industries.hospitality.examples.2')
      ]
    },
    {
      icon: ShoppingBag,
      name: t('industryShowcase.industries.retail.name'),
      description: t('industryShowcase.industries.retail.description'),
      examples: [
        t('industryShowcase.industries.retail.examples.0'),
        t('industryShowcase.industries.retail.examples.1'),
        t('industryShowcase.industries.retail.examples.2')
      ]
    },
    {
      icon: Factory,
      name: t('industryShowcase.industries.manufacturing.name'),
      description: t('industryShowcase.industries.manufacturing.description'),
      examples: [
        t('industryShowcase.industries.manufacturing.examples.0'),
        t('industryShowcase.industries.manufacturing.examples.1'),
        t('industryShowcase.industries.manufacturing.examples.2')
      ]
    },
    {
      icon: UtensilsCrossed,
      name: t('industryShowcase.industries.restaurants.name'),
      description: t('industryShowcase.industries.restaurants.description'),
      examples: [
        t('industryShowcase.industries.restaurants.examples.0'),
        t('industryShowcase.industries.restaurants.examples.1'),
        t('industryShowcase.industries.restaurants.examples.2')
      ]
    },
    {
      icon: Heart,
      name: t('industryShowcase.industries.healthcare.name'),
      description: t('industryShowcase.industries.healthcare.description'),
      examples: [
        t('industryShowcase.industries.healthcare.examples.0'),
        t('industryShowcase.industries.healthcare.examples.1'),
        t('industryShowcase.industries.healthcare.examples.2')
      ]
    },
    {
      icon: Building,
      name: t('industryShowcase.industries.corporate.name'),
      description: t('industryShowcase.industries.corporate.description'),
      examples: [
        t('industryShowcase.industries.corporate.examples.0'),
        t('industryShowcase.industries.corporate.examples.1'),
        t('industryShowcase.industries.corporate.examples.2')
      ]
    }
  ]
  
  return (
    <section className="py-24 bg-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-6"
          >
            <Hotel className="w-4 h-4" />
            {t('industryShowcase.badge')}
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('industryShowcase.title')}
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('industryShowcase.subtitle')}
          </motion.p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {industries.map((industry, index) => (
            <motion.div
              key={industry.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <industry.icon className="w-8 h-8 text-orange-600" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{industry.name}</h3>
              <p className="text-gray-600 mb-4">{industry.description}</p>
              
              <div className="space-y-2 mb-4">
                {industry.examples.map((example, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                    <span>{example}</span>
                  </div>
                ))}
              </div>
              

            </motion.div>
          ))}
        </div>

        {/* Use Case Example */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 border border-orange-200"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center text-center">
            <div>
              <Hotel className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">{t('industryShowcase.example.original.title')}</h4>
              <p className="text-gray-600 text-sm">
                {t('industryShowcase.example.original.description')}
              </p>
            </div>
            
            <div className="flex items-center justify-center">
              <ArrowRight className="w-8 h-8 text-orange-500" />
            </div>
            
            <div>
              <Building className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h4 className="font-bold text-gray-900 mb-2">{t('industryShowcase.example.universal.title')}</h4>
              <p className="text-gray-600 text-sm">
                {t('industryShowcase.example.universal.description')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}